import { useMemo, useState } from "react";
import { FilterCar, filterFleet, maxFilterPrice } from "utils/fleetFilter";
import { uniqueFleetClassNames } from "utils/fleetClasses";

/**
 * 1NOW-11129: the filter state of a Design B vehicle grid, derived in one
 * place. The list is never stored: it is computed from the cars, the ticked
 * classes and the price range together, so one filter cannot overwrite another.
 *
 * The price range is `null` until the renter moves it, and then follows the
 * fleet (0 to its dearest car). That also means there is no frame between the
 * cars arriving and an effect widening a [0, 0] range, which would have shown
 * an empty grid (and, from 1NOW-11132, "No vehicles match your filters").
 */
export const useFleetFilters = <T extends FilterCar>(
  cars: T[] | null | undefined,
  initialTypes: string[] = [],
  initialRange: number[] | null = null,
) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialTypes);
  const [pickedRange, setRangePrices] = useState<number[] | null>(initialRange);
  // Bumped by clearFilters so a filter bar that keeps its own checkbox state remounts clean.
  const [resetKey, setResetKey] = useState(0);

  const maxPrice = useMemo(() => maxFilterPrice(cars), [cars]);
  const types = useMemo(() => uniqueFleetClassNames(cars), [cars]);
  const rangePrices = pickedRange ?? [0, maxPrice];
  const filteredFleet = useMemo(
    () => filterFleet(cars, selectedTypes, [rangePrices[0], rangePrices[1]]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cars, selectedTypes, rangePrices[0], rangePrices[1]],
  );

  const clearFilters = () => {
    setSelectedTypes([]);
    setRangePrices(null);
    setResetKey((k) => k + 1);
  };

  return {
    types,
    selectedTypes,
    setSelectedTypes,
    rangePrices,
    setRangePrices,
    maxPrice,
    filteredFleet,
    clearFilters,
    resetKey,
  };
};
